import React, { useState, useMemo, useEffect, useCallback } from 'react';
import { useAuth } from '../components/auth/AuthProvider';
import { useDynamoUtils } from '../context/DynamoUtilsContext';
import { gradeExamAnswer } from '../utils/openAIService';
import { getUserProfile } from '../utils/profileUtils';
import { fetchExamQuestionsFromS3 } from '../utils/S3Utils';
import { getYearLevelsForState, getTopicsForStateAndYear, getDisplayNameForYearLevel } from '../config/examPrepConfig';
import '../assets/styles/ExamPrep.css';
import {
  Search,
  BarChart,
  CheckCircle,
  GraphUp,
  Circle,
  X,
  ChevronLeft,
  ChevronRight,
  Lightbulb,
  ArrowRight,
  Check,
  Paperclip
} from 'react-bootstrap-icons';
import FileUploadModal from '../components/FileUploadModal';

import { MarkdownContent } from '../components/MarkdownContent';

const ExamPrep = () => {
  const auth = useAuth();
  const dynamoUtilsRef = useDynamoUtils();
  const userId = auth.user?.sub;

  // User profile state
  // eslint-disable-next-line no-unused-vars
  const [_userProfile, setUserProfile] = useState(null);
  const [isLoadingProfile, setIsLoadingProfile] = useState(true);

  // Questions state - now fetched from S3
  const [questions, setQuestions] = useState([]);
  const [isLoadingQuestions, setIsLoadingQuestions] = useState(true);
  const [questionsError, setQuestionsError] = useState(null);

  // Progress state - add loading state for progress
  const [isLoadingProgress, setIsLoadingProgress] = useState(true);
  const [progressError, setProgressError] = useState(null);

  // Exam settings state
  const [selectedState, setSelectedState] = useState('NSW');
  const [selectedYearLevelKey, setSelectedYearLevelKey] = useState('11_standard');
  const [selectedTopic, setSelectedTopic] = useState('');
  const [selectedDifficulties, setSelectedDifficulties] = useState(['easy', 'medium', 'hard']);

  // Search and filter state
  const [searchQuery, setSearchQuery] = useState('');
  const [progressFilter, setProgressFilter] = useState('all'); // 'all', 'completed', 'incomplete', 'incorrect'

  // Modal state
  const [selectedQuestion, setSelectedQuestion] = useState(null);
  const [answer, setAnswer] = useState('');
  const [feedback, setFeedback] = useState(null);
  const [isMarking, setIsMarking] = useState(false);
  const [showHint, setShowHint] = useState(false);
  const [showAnswer, setShowAnswer] = useState(false);
  const [showFileUploadModal, setShowFileUploadModal] = useState(false);
  const [uploadedFileData, setUploadedFileData] = useState(null);

  // Track question progress (in real app, this would be persisted)
  const [questionProgress, setQuestionProgress] = useState({});

  // Load saved question progress from DynamoDB
  useEffect(() => {
    let isMounted = true; // Track component mount status
    
    const fetchProgress = async () => {
      console.log('ExamPrep: Starting progress fetch for userId:', userId);
      
      if (!userId) {
        console.log('ExamPrep: No userId available, skipping progress fetch');
        if (isMounted) setIsLoadingProgress(false);
        return;
      }

      if (!dynamoUtilsRef.current?.getExamProgressForUser) {
        console.log('ExamPrep: DynamoDB utils not available, waiting...');
        return; // Don't set loading to false, wait for context to be ready
      }

      if (isMounted) {
        setIsLoadingProgress(true);
        setProgressError(null);
      }
      
      try {
        console.log('ExamPrep: Fetching exam progress from DynamoDB...');
        const progress = await dynamoUtilsRef.current.getExamProgressForUser(userId);
        console.log('ExamPrep: Progress fetched successfully:', progress);
        
        if (isMounted) {
          if (progress) {
            setQuestionProgress(progress);
          } else {
            console.log('ExamPrep: No progress found for user');
            setQuestionProgress({});
          }
        }
      } catch (err) {
        console.error('ExamPrep: Error loading exam progress:', err);
        if (isMounted) {
          setProgressError('Failed to load your progress. Some features may not work correctly.');
          // Don't fail completely, just log the error
          setQuestionProgress({});
        }
      } finally {
        if (isMounted) {
          setIsLoadingProgress(false);
        }
      }
    };

    fetchProgress();
    
    // Cleanup function
    return () => {
      isMounted = false;
    };
  }, [userId, dynamoUtilsRef.current]); // Fixed dependency to trigger when context becomes available

  // Load user profile
  useEffect(() => {
    const loadProfile = async () => {
      if (userId) {
        try {
          const profile = await getUserProfile({ userId });
          if (profile) {
            setUserProfile(profile);
            
            // Set defaults from user profile
            if (profile.state) {
              setSelectedState(profile.state);
            }
            
                            // Set year level key from profile
                if (profile.yearMathCombination) {
                  setSelectedYearLevelKey(profile.yearMathCombination);
                }
          }
        } catch (error) {
          console.error('Error loading user profile:', error);
        }
      }
      setIsLoadingProfile(false);
    };

    loadProfile();
  }, [userId]);

  // Fetch exam questions when state or year level changes
  useEffect(() => {
    const fetchQuestions = async () => {
      setIsLoadingQuestions(true);
      setQuestionsError(null);
      
      console.log(`ExamPrep: Fetching questions for state="${selectedState}", yearLevelKey="${selectedYearLevelKey}"`);
      
      try {
        const fetchedQuestions = await fetchExamQuestionsFromS3(selectedState, selectedYearLevelKey);
        setQuestions(fetchedQuestions);
        
        if (fetchedQuestions.length === 0) {
          const displayName = getDisplayNameForYearLevel(selectedState, selectedYearLevelKey);
          setQuestionsError(`No questions found for ${selectedState} ${displayName}`);
        }
      } catch (error) {
        console.error('Error fetching questions:', error);
        setQuestionsError('Failed to load exam questions. Please try again.');
        setQuestions([]);
      } finally {
        setIsLoadingQuestions(false);
      }
    };

    fetchQuestions();
  }, [selectedState, selectedYearLevelKey]);

  const getQuestionStatus = useCallback((questionId) => {
    return questionProgress[questionId] || 'not-attempted';
  }, [questionProgress]);

  // Get available data based on selections
  const availableYearLevels = useMemo(() => {
    return getYearLevelsForState(selectedState);
  }, [selectedState]);

  const availableTopics = useMemo(() => {
    return getTopicsForStateAndYear(selectedState, selectedYearLevelKey);
  }, [selectedState, selectedYearLevelKey]);

  // Filter questions based on selections
  const filteredQuestions = useMemo(() => {
    let filtered = questions;

    // For now, we're using the NSW Year 11 Standard data
    // In the future, this would be loaded from S3 based on state/year level
    if (selectedTopic) {
      filtered = filtered.filter(q => q.topics.includes(selectedTopic));
    }

    // Filter by difficulty if specific difficulties are selected
    if (selectedDifficulties.length < 3) {
      filtered = filtered.filter(q => 
        selectedDifficulties.includes(q.difficulty || 'medium')
      );
    }

    // Filter by search query
    if (searchQuery.trim()) {
      const query = searchQuery.toLowerCase();
      filtered = filtered.filter(q => 
        q.question.toLowerCase().includes(query) ||
        q.topics.some(topic => topic.toLowerCase().includes(query)) ||
        (q.hint && q.hint.toLowerCase().includes(query))
      );
    }

    // Filter by progress status
    if (progressFilter !== 'all') {
      filtered = filtered.filter(q => {
        const status = getQuestionStatus(q.id);
        switch (progressFilter) {
          case 'completed':
            return status === 'correct';
          case 'incomplete':
            return status === 'not-attempted';
          case 'incorrect':
            return status === 'incorrect';
          default:
            return true;
        }
      });
    }

    // Sort by difficulty: easy -> medium -> hard
    const difficultyOrder = { 'easy': 1, 'medium': 2, 'hard': 3 };
    filtered.sort((a, b) => {
      const difficultyA = difficultyOrder[a.difficulty || 'medium'];
      const difficultyB = difficultyOrder[b.difficulty || 'medium'];
      return difficultyA - difficultyB;
    });

    return filtered;
  }, [questions, selectedTopic, selectedDifficulties, searchQuery, progressFilter, getQuestionStatus]);

  // Get topic counts for sidebar
  const topicCounts = useMemo(() => {
    const counts = {};
    availableTopics.forEach(topic => {
      const topicQuestions = questions.filter(q => q.topics.includes(topic));
      const completedCount = topicQuestions.filter(q => getQuestionStatus(q.id) === 'correct').length;
      counts[topic] = {
        total: topicQuestions.length,
        completed: completedCount,
        percentage: topicQuestions.length > 0 ? Math.round((completedCount / topicQuestions.length) * 100) : 0
      };
    });
    const allQuestionsCompleted = questions.filter(q => getQuestionStatus(q.id) === 'correct').length;
    counts['All Topics'] = {
      total: questions.length,
      completed: allQuestionsCompleted,
      percentage: questions.length > 0 ? Math.round((allQuestionsCompleted / questions.length) * 100) : 0
    };
    return counts;
  }, [questions, availableTopics, getQuestionStatus]);

  const handleQuestionClick = (question) => {
    setSelectedQuestion(question);
    setAnswer('');
    setFeedback(null);
    setShowHint(false);
    setShowAnswer(false);
    setUploadedFileData(null); // Reset uploaded file data for new question
  };

  const closeModal = () => {
    setSelectedQuestion(null);
    setAnswer('');
    setFeedback(null);
    setShowHint(false);
    setShowAnswer(false);
    setUploadedFileData(null); // Reset uploaded file data when closing modal
  };

  const navigateToQuestion = (direction) => {
    if (!selectedQuestion) return;
    
    const currentIndex = filteredQuestions.findIndex(q => q.id === selectedQuestion.id);
    let newIndex;
    
    if (direction === 'next') {
      newIndex = currentIndex < filteredQuestions.length - 1 ? currentIndex + 1 : 0;
    } else {
      newIndex = currentIndex > 0 ? currentIndex - 1 : filteredQuestions.length - 1;
    }
    
    const newQuestion = filteredQuestions[newIndex];
    if (newQuestion) {
      setSelectedQuestion(newQuestion);
      setAnswer('');
      setFeedback(null);
      setShowHint(false);
      setShowAnswer(false);
      setUploadedFileData(null); // Reset uploaded file data for new question
    }
  };

  const handleMark = async (revealAnswer) => {
    if (!selectedQuestion) return;
    
    setIsMarking(true);
    
    try {
      let token = auth.user?.tokens?.idToken || null;
      if (!token && typeof auth.getIdToken === 'function') {
        token = await auth.getIdToken();
      }
      
      if (!token) {
        setFeedback({ 
          correct: false, 
          feedback: 'Authentication error. Please try logging in again.' 
        });
        setIsMarking(false);
        return;
      }

      // Combine user text input and uploaded file content
      let combinedAnswer = '';
      if (answer.trim()) {
        combinedAnswer += `User Text Input: ${answer.trim()}`;
      }
      if (uploadedFileData?.text) {
        if (combinedAnswer) combinedAnswer += '\n\n';
        combinedAnswer += `User Uploaded File: ${uploadedFileData.text}`;
      }
      
      // Use the original answer if no combination is needed
      const finalAnswer = combinedAnswer || answer;

      const result = await gradeExamAnswer({
        token,
        question: selectedQuestion.question,
        correctAnswer: selectedQuestion.answer,
        userAnswer: finalAnswer, // Use combined answer
        revealAnswer,
        userId,
        dynamoUtils: dynamoUtilsRef.current
      });

      setShowAnswer(revealAnswer);
      setFeedback(result);
      
      // Update question progress
      setQuestionProgress(prev => ({
        ...prev,
        [selectedQuestion.id]: result.correct ? 'correct' : 'incorrect'
      }));

      if (dynamoUtilsRef.current?.saveExamQuestionProgress) {
        try {
          await dynamoUtilsRef.current.saveExamQuestionProgress(
            userId,
            selectedQuestion.id,
            result.correct ? 'correct' : 'incorrect'
          );
        } catch (err) {
          console.error('Error saving exam progress:', err);
        }
      }
    } catch (error) {
      console.error('Error grading answer:', error);
      setFeedback({
        correct: false,
        feedback: 'An error occurred while grading your answer. Please try again.'
      });
    } finally {
      setIsMarking(false);
    }
  };

  const handleFileUploadClick = () => {
    setShowFileUploadModal(true);
  };

  const handleFileUploadSuccess = (fileData) => {
    setShowFileUploadModal(false);
    // Store the uploaded file data in memory instead of populating the textbox
    setUploadedFileData(fileData);
  };

  const handleDifficultyToggle = (difficulty) => {
    setSelectedDifficulties(prev => {
      const newSelection = prev.includes(difficulty)
        ? prev.filter(d => d !== difficulty)
        : [...prev, difficulty];
      
      // Ensure at least one difficulty is selected
      return newSelection.length === 0 ? [difficulty] : newSelection;
    });
  };

  // Update the loading condition to include questions and progress loading
  if (isLoadingProfile || isLoadingQuestions || isLoadingProgress) {
    return (
      <div className="exam-prep-container">
        <div className="loading-state">
          <div className="loading-spinner"></div>
          <p>
            {isLoadingProfile && 'Loading your exam preparation...'}
            {!isLoadingProfile && isLoadingQuestions && 'Loading exam questions...'}
            {!isLoadingProfile && !isLoadingQuestions && isLoadingProgress && 'Loading your progress...'}
          </p>
        </div>
      </div>
    );
  }

  // Handle questions error state
  if (questionsError) {
    return (
      <div className="exam-prep-container">
        <div className="error-state">
          <h3>Unable to load exam questions</h3>
          <p>{questionsError}</p>
          <button 
            className="retry-button"
            onClick={() => {
              setIsLoadingQuestions(true);
              setQuestionsError(null);
              // Trigger refetch by updating state
              setSelectedState(prev => prev);
            }}
          >
            Try Again
          </button>
        </div>
      </div>
    );
  }

  return (
      <div className="exam-prep-container">
        {/* Progress Error Banner */}
        {progressError && (
          <div className="progress-error-banner">
            <div className="error-message">
              ⚠️ {progressError}
            </div>
            <button 
              className="retry-progress-button"
              onClick={() => {
                setProgressError(null);
                setIsLoadingProgress(true);
                // Trigger progress refetch by updating userId state
                if (userId && dynamoUtilsRef.current?.getExamProgressForUser) {
                  dynamoUtilsRef.current.getExamProgressForUser(userId)
                    .then(progress => {
                      if (progress) {
                        setQuestionProgress(progress);
                      }
                      setIsLoadingProgress(false);
                    })
                    .catch(err => {
                      console.error('Retry failed:', err);
                      setIsLoadingProgress(false);
                    });
                }
              }}
            >
              Retry Loading Progress
            </button>
          </div>
        )}

        {/* Top Banner */}
        <div className="exam-prep-banner">
          <div className="banner-left">
            <h1 className="banner-title">Exam Practice</h1>
            <div className="banner-subtitle">
              {selectedState} {getDisplayNameForYearLevel(selectedState, selectedYearLevelKey)}
            </div>
          </div>

          <div className="banner-center">
            <div className="search-container">
              <Search className="search-icon" />
              <input
                type="text"
                className="search-input"
                placeholder="Search questions and topics..."
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
              />
            </div>
            
            {/* Difficulty Filter */}
            <div className="banner-difficulty-filters">
              {['easy', 'medium', 'hard'].map(difficulty => (
                <button
                  key={difficulty}
                  className={`banner-difficulty-btn ${selectedDifficulties.includes(difficulty) ? 'active' : ''} ${difficulty}`}
                  onClick={() => handleDifficultyToggle(difficulty)}
                >
                  {difficulty.charAt(0).toUpperCase() + difficulty.slice(1)}
                </button>
              ))}
            </div>
          </div>

          <div className="banner-stats">
            <div className="banner-stat">
              <BarChart className="banner-stat-icon" />
              <div className="banner-stat-content">
                <span className="banner-stat-value">{filteredQuestions.length}</span>
                <span className="banner-stat-label">Questions</span>
              </div>
            </div>
            <div className="banner-stat">
              <CheckCircle className="banner-stat-icon" />
              <div className="banner-stat-content">
                <span className="banner-stat-value">
                  {filteredQuestions.filter(q => getQuestionStatus(q.id) === 'correct').length}
                </span>
                <span className="banner-stat-label">Completed</span>
              </div>
            </div>
            <div className="banner-stat">
              <GraphUp className="banner-stat-icon" />
              <div className="banner-stat-content">
                <span className="banner-stat-value">
                  {filteredQuestions.length > 0 ? Math.round((filteredQuestions.filter(q => getQuestionStatus(q.id) === 'correct').length / filteredQuestions.length) * 100) : 0}%
                </span>
                <span className="banner-stat-label">Progress</span>
              </div>
            </div>
          </div>
        </div>

        {/* Main Content */}
        <div className="exam-prep-main">
          {/* Left Sidebar */}
          <div className="exam-prep-sidebar">
            {/* Topics Section */}
            <div className="sidebar-section">
              <h3 className="sidebar-title">Topics</h3>
              <div className="topic-list">
                <div
                  className={`topic-item all-topics ${!selectedTopic ? 'selected' : ''}`}
                  onClick={() => setSelectedTopic('')}
                >
                  <span className="topic-name">All Topics</span>
                  <div className="topic-meta">
                    <span className="topic-count">{topicCounts['All Topics']?.total || 0}</span>
                    <div className={`topic-progress-ring ${topicCounts['All Topics']?.percentage > 0 ? 'has-progress' : ''}`}>
                      <div 
                        className="progress-fill" 
                        style={{ '--progress': `${topicCounts['All Topics']?.percentage || 0}%` }}
                      ></div>
                      <span className="progress-text">{topicCounts['All Topics']?.percentage || 0}%</span>
                    </div>
                  </div>
                </div>
                {availableTopics.map(topic => (
                  <div
                    key={topic}
                    className={`topic-item ${selectedTopic === topic ? 'selected' : ''}`}
                    onClick={() => setSelectedTopic(topic)}
                  >
                    <span className="topic-name">{topic}</span>
                    <div className="topic-meta">
                      <span className="topic-count">{topicCounts[topic]?.total || 0}</span>
                      <div className={`topic-progress-ring ${(topicCounts[topic]?.percentage || 0) > 0 ? 'has-progress' : ''}`}>
                        <div 
                          className="progress-fill" 
                          style={{ '--progress': `${topicCounts[topic]?.percentage || 0}%` }}
                        ></div>
                        <span className="progress-text">{topicCounts[topic]?.percentage || 0}%</span>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            </div>

            {/* Progress Filter Section */}
            <div className="sidebar-section">
              <h3 className="sidebar-title">Progress Filter</h3>
              <div className="progress-filters">
                {[
                  { id: 'all', label: 'All Questions', icon: <BarChart /> },
                  { id: 'completed', label: 'Completed', icon: <CheckCircle /> },
                  { id: 'incomplete', label: 'Not Attempted', icon: <Circle /> },
                  { id: 'incorrect', label: 'Incorrect', icon: <X /> }
                ].map(filter => (
                  <div
                    key={filter.id}
                    className={`progress-filter ${progressFilter === filter.id ? 'selected' : ''}`}
                    onClick={() => setProgressFilter(filter.id)}
                  >
                    <span className="filter-icon">{filter.icon}</span>
                    <span className="filter-label">{filter.label}</span>
                  </div>
                ))}
              </div>
            </div>


          </div>

          {/* Content Area */}
          <div className="exam-prep-content">
            {filteredQuestions.length === 0 ? (
              <div className="empty-state">
                <Search className="empty-state-icon" />
                <h3>No questions found</h3>
                <p>Try adjusting your filters or selecting a different topic</p>
              </div>
            ) : (
              <div className="questions-grid">
                {filteredQuestions.map((question, index) => (
                  <div
                    key={question.id}
                    className={`question-card ${getQuestionStatus(question.id) === 'correct' ? 'completed' : ''}`}
                    onClick={() => handleQuestionClick(question)}
                  >
                    <div className="question-card-header">
                      <div className="question-title">
                        {question.topics[0]} Question {index + 1}
                      </div>
                    </div>
                    <div className="question-preview">
                      <MarkdownContent content={question.question} />
                    </div>
                    <div className="question-card-footer">
                      <div className="question-status">
                        <div className={`status-icon ${getQuestionStatus(question.id)}`}>
                          {getQuestionStatus(question.id) === 'correct' && <Check />}
                          {getQuestionStatus(question.id) === 'incorrect' && <X />}
                        </div>
                        <span className="status-label">
                          {getQuestionStatus(question.id) === 'not-attempted' && 'Not attempted'}
                          {getQuestionStatus(question.id) === 'correct' && 'Correct'}
                          {getQuestionStatus(question.id) === 'incorrect' && 'Incorrect'}
                        </span>
                      </div>
                      <div className={`question-difficulty-badge ${question.difficulty || 'medium'}`}>
                        {(question.difficulty || 'medium').toUpperCase()}
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
        </div>

        {/* Question Modal */}
        {selectedQuestion && (
          <div className="question-modal-overlay" onClick={closeModal}>
            <div className="question-modal" onClick={(e) => e.stopPropagation()}>
              <div className="modal-header">
                <div className="modal-header-left">
                  <h2 className="modal-title">
                    Question {filteredQuestions.findIndex(q => q.id === selectedQuestion.id) + 1} of {filteredQuestions.length}
                  </h2>
                  <div className="modal-question-meta">
                    <span className={`modal-difficulty-badge ${selectedQuestion.difficulty || 'medium'}`}>
                      {(selectedQuestion.difficulty || 'medium').toUpperCase()}
                    </span>
                    <span className="modal-topics">{selectedQuestion.topics.join(', ')}</span>
                  </div>
                </div>
                <div className="modal-header-right">
                  <button 
                    className="modal-nav-btn"
                    onClick={() => navigateToQuestion('prev')}
                    disabled={filteredQuestions.length <= 1}
                    title="Previous question"
                  >
                    <ChevronLeft />
                  </button>
                  <button 
                    className="modal-nav-btn"
                    onClick={() => navigateToQuestion('next')}
                    disabled={filteredQuestions.length <= 1}
                    title="Next question"
                  >
                    <ChevronRight />
                  </button>
                  <button className="modal-close" onClick={closeModal}>
                    <X />
                  </button>
                </div>
              </div>
              
              <div className="modal-content">
                <div className="modal-question">
                  <MarkdownContent content={selectedQuestion.question} />
                </div>

                <div className="modal-answer-section">
                  <textarea
                    className="modal-answer-textarea"
                    value={answer}
                    onChange={(e) => setAnswer(e.target.value)}
                    placeholder="Type your answer here..."
                    disabled={feedback && feedback.correct}
                  />
                  <button
                    className={`modal-upload-button ${uploadedFileData ? 'uploaded' : ''}`}
                    onClick={handleFileUploadClick}
                  >
                    <Paperclip /> {uploadedFileData ? 'Working successfully uploaded ✓' : 'Upload Working'}
                  </button>
                  {uploadedFileData && (
                    <div className="modal-uploaded-file-info">
                      <small>📄 Uploaded: {uploadedFileData.fileName}</small>
                    </div>
                  )}
                </div>

                {/* Hint Section */}
                {selectedQuestion.hint && (
                  <div className="modal-hint-section">
                    {!showHint ? (
                      <button 
                        className="modal-hint-button"
                        onClick={() => setShowHint(true)}
                      >
                        <Lightbulb /> Show Hint
                      </button>
                    ) : (
                      <div className="modal-hint-content">
                        <p className="modal-hint-text">
                          <strong>Hint:</strong> {selectedQuestion.hint}
                        </p>
                      </div>
                    )}
                  </div>
                )}

                {/* Action Buttons */}
                {!feedback && (
                  <div className="modal-actions">
                    <button
                      className="modal-button primary"
                      onClick={() => handleMark(false)}
                      disabled={isMarking || (!answer.trim() && !uploadedFileData?.text)}
                    >
                      {isMarking ? 'Checking...' : 'Get Feedback'}
                    </button>
                    <button
                      className="modal-button secondary"
                      onClick={() => handleMark(true)}
                      disabled={isMarking || (!answer.trim() && !uploadedFileData?.text)}
                    >
                      {isMarking ? 'Checking...' : 'Mark & Show Answer'}
                    </button>
                  </div>
                )}

                {/* Feedback */}
                {feedback && (
                  <div className="modal-feedback">
                    <div className="modal-feedback-header">
                      <div className="modal-feedback-icon">
                        {feedback.correct ? <CheckCircle /> : <X />}
                      </div>
                      <div className={`modal-feedback-status ${feedback.correct ? 'correct' : 'incorrect'}`}>
                        {feedback.correct ? 'Correct!' : 'Not quite right'}
                      </div>
                    </div>
                    
                    <div className="modal-feedback-text">
                      {feedback.feedback}
                    </div>

                    {showAnswer && (
                      <div className="modal-correct-answer">
                        <strong>Correct Answer:</strong>
                        <p>{selectedQuestion.answer}</p>
                      </div>
                    )}

                    <button 
                      className="modal-next-button"
                      onClick={closeModal}
                    >
                      Close
                    </button>
                    
                    {filteredQuestions.length > 1 && (
                      <button 
                        className="modal-nav-button"
                        onClick={() => navigateToQuestion('next')}
                      >
                        Next Question <ArrowRight />
                      </button>
                    )}
                  </div>
                )}
              </div>
            </div>
          </div>
        )}
        {showFileUploadModal && (
          <FileUploadModal
            onClose={() => setShowFileUploadModal(false)}
            onSuccess={handleFileUploadSuccess}
          />
        )}
      </div>
  );
};

export default ExamPrep;
